import React, { memo, useCallback, useState } from 'react'

import { AIGeneratedGradientFilled, Button, Icon, Trash } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { number, oneOfType, shape, string } from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useDeleteImageRun from 'Pods/ThreeDStudio/Hooks/useDeleteImageRun'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import DownloadButton from 'Pods/ThreeDStudio/Shared/DownloadButton/DownloadButton'
import { closeFullImageEditor } from 'Pods/ThreeDStudio/Store/actions/ui'

const trashIcon = <Icon svgComponent={Trash} size={iconSize} notSpaced color="darker" />
const generateThreeDIcon = <Icon svgComponent={AIGeneratedGradientFilled} size={iconSize} notSpaced />

// Right-hand controls of the full-width editor header. Figma draws five; Share is left
// out because its link comes from buildSharedRunPathname and an opened image has no
// public URL yet, so it would be dead UI. Generate 3D ships disabled - it is wired in
// 86cbhnp24, which owns both of its entry points.
function HeaderActions({ image = null, runId = null, imageName = '' }) {
  const t = useTranslation()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const handleOpenDelete = useCallback(() => setIsDeleteModalOpen(true), [])

  const handleCloseDelete = useCallback(() => setIsDeleteModalOpen(false), [])

  // Deleting the image the editor is showing leaves nothing to edit, so the surface goes
  // with it - which also unmounts the confirm modal.
  const { deleteImage, isLoading: isDeleting } = useDeleteImageRun({ onSuccess: closeFullImageEditor })

  const handleConfirmDelete = useCallback(() => deleteImage(runId), [deleteImage, runId])

  return (
    <div className="full-image-editor__actions">
      <Button
        type="secondary"
        size="small"
        variant="only-icon"
        actionType="button"
        customClass="full-image-editor__action"
        icon={trashIcon}
        aria-label={t('fullImageEditor.actions.delete')}
        disabled={runId == null || isDeleting}
        onClick={handleOpenDelete}
      />
      {image && (
        <DownloadButton result={image} customClass="full-image-editor__action" />
      )}
      <Button
        type="secondary"
        size="small"
        variant="with-icon"
        actionType="button"
        customClass="full-image-editor__action full-image-editor__action--ai"
        icon={generateThreeDIcon}
        disabled
        aria-disabled="true"
      >
        <span className="full-image-editor__action-label cgt-body-base-semibold">
          {t('fullImageEditor.actions.generateThreeD')}
        </span>
      </Button>
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="image"
          itemName={imageName}
          isSubmitDisabled={isDeleting}
          onSubmit={handleConfirmDelete}
          onCancel={handleCloseDelete}
        />
      )}
    </div>
  )
}

HeaderActions.propTypes = {
  image: shape({
    id: oneOfType([string, number]).isRequired,
    url: string.isRequired,
  }),
  runId: oneOfType([string, number]),
  imageName: string,
}

export default memo(HeaderActions)
